import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";

const baseButtonStyles =
  "font-medium text-sm transition-colors flex items-center justify-center";

const buttonVariants = {
  primary: "bg-brand hover:bg-indigo-700 text-white dark:text-white",
  success: "bg-success hover:bg-green-500 text-white",
  danger: "bg-danger hover:bg-red-700 text-white",
  secondary:
    "border-brand text-brand hover:text-white hover:bg-brand",
  light:
    "bg-tertiary hover:bg-gray-200 dark:hover:bg-gray-600 text-primary",
  outline:
    "border border-brand text-brand hover:bg-brand hover:text-white",
  outlineGray:
    "border border-secondary text-secondary hover:bg-secondary",
};

const buttonSizes = {
  xs: "w-10 h-10 rounded-full p-0",
  sm: "w-16 h-16 rounded-full p-0",
  md: "w-full py-2 px-4 rounded-full",
  lg: "flex-1 py-2 px-4 rounded-full",
};

export const BaseScribephoneButton = ({
  children,
  icon,
  variant = "primary",
  size = "md",
  iconClassName = "mr-2",
  disabled = false,
  className = "",
  dataTestId,
  ...props
}) => {
  const disabledStyles = disabled
    ? "disabled:bg-disabled disabled:cursor-not-allowed"
    : "";
  const sizeStyles = buttonSizes[size] || buttonSizes.md;
  const variantStyles =
    buttonVariants[variant] || buttonVariants.primary;

  const combinedStyles = `${baseButtonStyles} ${sizeStyles} ${variantStyles} ${disabledStyles} ${className}`;

  return (
    <button
      className={combinedStyles}
      disabled={disabled}
      {...props}
      data-testid={dataTestId}
    >
      {icon && (
        <FontAwesomeIcon
          icon={icon}
          className={children ? iconClassName : ""}
        />
      )}
      {children}
    </button>
  );
};

export const CallButton = ({ icon, ...props }) => (
  <BaseScribephoneButton
    variant="success"
    size="sm"
    icon={icon}
    {...props}
    dataTestId="scribephone-call-button"
  />
);

export const SettingsButton = ({ icon, ...props }) => (
  <BaseScribephoneButton
    variant="primary"
    size="xs"
    icon={icon}
    {...props}
    dataTestId="scribephone-settings-button"
  />
);

export const EndCallButton = ({
  children = "End Call",
  ...props
}) => (
  <BaseScribephoneButton
    variant="danger"
    size="md"
    {...props}
    dataTestId="scribephone-end-call-button"
  >
    {children}
  </BaseScribephoneButton>
);

export const MuteButton = ({ isMuted, children, ...props }) => (
  <BaseScribephoneButton
    variant={isMuted ? "danger" : "primary"}
    size="lg"
    {...props}
  >
    {children}
  </BaseScribephoneButton>
);

export const DialpadButton = ({
  number,
  letters,
  onClick,
  allowSpecialChars = false,
  isActive = false,
}) => {
  const isSpecialChar = number === "*" || number === "#";
  const isDisabled = isSpecialChar && !allowSpecialChars;

  const getButtonClass = () => {
    const baseClass =
      "w-16 h-16 rounded-full transition-all flex flex-col items-center justify-center shadow-lg";

    if (isDisabled) {
      return `${baseClass} bg-brand-subtle text-secondary cursor-default`;
    }

    if (isActive) {
      return `${baseClass} bg-brand text-white scale-95 transform`;
    }

    return `${baseClass} bg-brand-subtle hover:bg-indigo-200 dark:hover:bg-brand text-primary`;
  };

  const handleClick = () => {
    if (!isDisabled) {
      onClick(number);
    }
  };

  return (
    <button onClick={handleClick} className={getButtonClass()}>
      <span className="text-xl font-semibold">{number}</span>
      {letters && (
        <span className="text-xs text-secondary">{letters}</span>
      )}
    </button>
  );
};
